import { h, setChildren } from "../core/dom.js";
import { icon } from "../lib/icons.js";
import { navigate } from "../core/router.js";
import { openModal } from "./ui.js";
import { profile } from "../store.js";

// The Dashboard "Coach" card — surfaces the top science-coaching insights produced by the
// server-side deterministic rules engine (GET /api/dashboard → `coaching`). Each insight is a
// concrete, numbered, evidence-based tip; tapping "Why?" opens the cited claim behind it.
//
// Dismissal is client-only (localStorage) so a user can clear a tip without a schema change —
// the same insight `key` won't re-show until the underlying situation changes the key.

function dismissKey() {
  const uid = profile()?.id;
  return uid ? `wt_coach_dismissed:${uid}` : "wt_coach_dismissed";
}
const MAX_VISIBLE = 1;

function loadDismissed() {
  try { return new Set(JSON.parse(localStorage.getItem(dismissKey())) || []); } catch { return new Set(); }
}
function saveDismissed(set) {
  try { localStorage.setItem(dismissKey(), JSON.stringify([...set])); } catch {}
}

const SEVERITY = {
  warn: { color: "var(--danger)", label: "Heads up" },
  suggest: { color: "var(--accent)", label: "Tip" },
  info: { color: "var(--text-2)", label: "Note" },
};

// Returns the section node, or null when there's nothing (un-dismissed) to show.
export function coachCard(coaching) {
  const all = (coaching && Array.isArray(coaching.insights) ? coaching.insights : []).filter((i) => i && i.message);
  if (!all.length) return null;

  const dismissed = loadDismissed();
  // Prune stale dismissals (insight keys that no longer appear) so the store doesn't grow forever.
  const liveKeys = new Set(all.map((i) => i.key));
  let pruned = false;
  for (const k of [...dismissed]) if (!liveKeys.has(k)) { dismissed.delete(k); pruned = true; }
  if (pruned) saveDismissed(dismissed);

  const root = h("div");
  let showAll = false;
  // Progressive disclosure: rows render as one-line headlines; only expanded rows show the full
  // guidance + "Why?". The top insight starts expanded (it's the most urgent — the server ranks
  // data-driven tips above static education); the rest are a tap away instead of a wall of text.
  const openKeys = new Set();
  let seeded = false;

  function render() {
    const visible = all.filter((i) => !dismissed.has(i.key));
    if (!visible.length) { setChildren(root); return; }
    if (!seeded) { openKeys.add(visible[0].key); seeded = true; }
    const shown = showAll ? visible : visible.slice(0, MAX_VISIBLE);
    const extra = visible.length - shown.length;

    setChildren(root,
      h("div", { class: "section-title row-between" },
        h("span", { class: "hstack", style: { gap: "7px", alignItems: "center" } },
          icon("chart", { size: 16, className: "icon muted" }), "Coach"),
        h("span", { class: "dim", style: { fontSize: ".72rem", fontWeight: 600 } }, "Evidence-based")
      ),
      h("div", { class: "card" },
        ...shown.map(insightRow),
        extra > 0
          ? h("button", { class: "link-btn dim", style: { fontSize: ".82rem", marginTop: "6px" }, onClick: () => { showAll = true; render(); } }, `See all ${visible.length} insights`)
          : showAll && visible.length > 1
            ? h("button", { class: "link-btn dim", style: { fontSize: ".82rem", marginTop: "6px" }, onClick: () => { showAll = false; render(); } }, "Show top insight")
            : null
      )
    );
  }

  function insightRow(ins) {
    const sev = SEVERITY[ins.severity] || SEVERITY.info;
    const open = openKeys.has(ins.key);
    const toggle = () => { open ? openKeys.delete(ins.key) : openKeys.add(ins.key); render(); };
    const chev = icon("chevronDown", { size: 14, className: "icon muted" });
    chev.style.cssText = `flex:0 0 auto;transition:transform .15s ease;${open ? "transform:rotate(180deg);" : ""}`;
    return h("div", { class: "row-between coach-row", style: { padding: "9px 0", gap: "10px", alignItems: "flex-start" } },
      h("div", { class: "hstack", style: { gap: "9px", alignItems: "flex-start", flex: "1", minWidth: 0 } },
        h("span", { style: { marginTop: "5px", flex: "0 0 auto", width: "8px", height: "8px", borderRadius: "50%", background: sev.color } }),
        h("div", { style: { minWidth: 0, flex: "1" } },
          h("div", { class: "hstack card-tap", style: { gap: "6px", alignItems: "center" }, onClick: toggle },
            h("div", { style: { fontWeight: 700, fontSize: ".9rem", flex: "1" } }, ins.title || sev.label), chev),
          open ? h("div", { class: "dim", style: { fontSize: ".82rem", lineHeight: "1.45", marginTop: "2px" } }, ins.message) : null,
          open ? h("button",{class:"link-btn",onClick:()=>navigate(coachAction(ins).path)},coachAction(ins).label) : null,
          open && ins.citations && ins.citations.length
            ? h("button", { class: "link-btn", style: { fontSize: ".78rem", marginTop: "5px", color: "var(--accent)" }, onClick: () => openWhy(ins) }, "Why?")
            : null
        )
      ),
      h("button", { class: "icon-btn", "aria-label": "Dismiss tip", style: { flex: "0 0 auto", opacity: ".6" }, onClick: () => { dismissed.add(ins.key); saveDismissed(dismissed); render(); } },
        icon("x", { size: 16 }))
    );
  }

  render();
  return root;
}

function coachAction(ins) {
  if(ins.claim_id==="str-plateau-stall" && /^str-plateau-stall:\d+$/.test(ins.key))return {label:"View this lift",path:`/exercise/${ins.key.split(":")[1]}`};
  if(ins.claim_id?.startsWith("program-"))return {label:"Review my program",path:"/programs"};
  if(ins.domain==="fat_loss")return {label:"Review bodyweight trend",path:"/weight"};
  return {label:"Review my training",path:"/week"};
}

// The cited "why?" modal — what the tip is, the evidence summary, and every source behind it.
function openWhy(ins) {
  openModal({
    title: ins.title || "Why this tip?",
    content: h("div", null,
      h("h4",null,ins.kind==="edu"?"General guidance":ins.claim_id?.startsWith("program-")?"Observation from your planned targets":"Observation from your logs"),
      h("p", { style: { fontSize: ".9rem", lineHeight: "1.5", marginTop: "8px" } }, ins.message),
      h("h4",{class:"mt"},"Assumptions & limits"),
      h("p",{class:"dim",style:{fontSize:".875rem",marginTop:"8px"}},ins.kind==="edu"?"This is general guidance selected for your profile or training goal; it does not establish your individual response.":"This uses the entries and profile details available to Kashtu. Missing logs, unrecorded effort, exercise differences, and how you feel can change the interpretation. Set-volume guidance assumes an unlogged RPE represents a working set."),
      ins.why
        ? h("p", { class: "dim", style: { fontSize: ".85rem", lineHeight: "1.5" } }, ins.why)
        : null,
      h("div", { class: "section-title", style: { fontSize: ".78rem", marginTop: "10px" } }, "Evidence"),
      h("div", null, ...(ins.citations || []).map((c) =>
        h("div", { style: { padding: "9px 0", borderTop: "1px solid var(--border)" } },
          h("div", { style: { fontWeight: 700, fontSize: ".84rem" } }, c.source),
          c.finding ? h("div", { class: "dim", style: { fontSize: ".82rem", lineHeight: "1.45", marginTop: "2px" } }, c.finding) : null,
          c.url ? h("a", { href: c.url, target: "_blank", rel: "noopener noreferrer", style: { fontSize: ".8rem", color: "var(--accent)", display: "inline-block", marginTop: "4px" } }, "View source ↗") : null
        ))),
      h("p", { class: "dim", style: { fontSize: ".74rem", lineHeight: "1.45", marginTop: "12px" } },
        "General evidence-based guidance from your logged training — not medical advice. Numbers are population averages and vary by person.")
    ),
  });
}
